import React, { useState, useRef, useEffect } from 'react';
import { formatExamDate } from '../generators/utils/pdfHelpers';
import { useSupervisors } from '../hooks/useSupervisors';

const SUPERVISOR_OTHER = '__other__';

/**
 * PatientInfo — shared patient information block used by all form components.
 *
 * Props:
 *   formData      {object}   - Current form data from useUltrasoundForm
 *   updateField   {function} - Hook's updateField method
 *   technologists {string[]} - Available technologist names
 *   fieldErrors   {object}   - Optional: { firstName, lastName, mrn, examDate } error strings
 *   examLabel     {string}   - Optional: label shown in read-only "Exam Type" field
 *   showCallReport {boolean} - Optional, default true: whether to render the Call
 *                              Report checkbox/phone field. Some exam types (e.g.
 *                              Lung Cancer Screening) never require a call report.
 *   technologistLabel {string} - Optional, default "Technologist": label for the
 *                              staff-member field. Lung Cancer Screening (nurse-
 *                              performed) passes "Nurse" instead.
 *
 * Student accounts additionally get a required "Supervising Sonographer" field.
 * This component marks the worksheet itself (formData.studentWorksheet = true)
 * so validation in useUltrasoundForm and the PDF patient box ("(Student)" +
 * supervisor row) work for every form without per-form changes.
 */
function PatientInfo({ formData, updateField, technologists, fieldErrors = {}, examLabel, showCallReport = true, technologistLabel = 'Technologist' }) {
  const [collapsed, setCollapsed] = useState(false);
  const { isStudent, supervisors } = useSupervisors();
  // "Other" = free-text name not on the list (e.g. a traveler or new hire)
  const [supervisorOther, setSupervisorOther] = useState(false);

  // Tag the worksheet as student-completed; re-applies after Clear Form
  // resets formData to its initial state
  useEffect(() => {
    if (isStudent && !formData.studentWorksheet) {
      updateField('studentWorksheet', true);
    }
  }, [isStudent, formData.studentWorksheet, updateField]);

  const supervisor = formData.supervisingSonographer || '';
  // A restored draft may hold a typed name that isn't in the list
  const showSupervisorOther = supervisorOther || (!!supervisor && supervisors.length > 0 && !supervisors.includes(supervisor));
  const needsSupervisor = !!formData.studentWorksheet;

  const [flashCollapseBtn, setFlashCollapseBtn] = useState(false);
  const containerRef = useRef(null);
  const wasReadyRef = useRef(false);

  const hasAllRequired = !!(
    formData.firstName && formData.firstName.trim() &&
    formData.lastName && formData.lastName.trim() &&
    formData.mrn && formData.mrn.trim() &&
    formData.examDate &&
    (!needsSupervisor || supervisor.trim())
  );
  const hasRequiredFieldError = !!(
    fieldErrors.firstName || fieldErrors.lastName || fieldErrors.mrn || fieldErrors.examDate ||
    fieldErrors.supervisingSonographer
  );
  // Collapsing is only ever in effect once all required fields are filled and
  // valid — if a field gets cleared or fails validation after collapsing, this
  // forces the full form back open so the error stays visible.
  const isCollapsed = collapsed && hasAllRequired && !hasRequiredFieldError;

  // Briefly flash the Collapse button the moment it becomes usable, so it's
  // obvious it just went from grayed-out to active rather than always having
  // been there unnoticed.
  useEffect(() => {
    const wasReady = wasReadyRef.current;
    wasReadyRef.current = hasAllRequired;
    if (hasAllRequired && !wasReady) {
      setFlashCollapseBtn(true);
      const timer = setTimeout(() => setFlashCollapseBtn(false), 1800);
      return () => clearTimeout(timer);
    }
  }, [hasAllRequired]);

  // Auto-collapse the moment focus moves to something outside this block
  // (e.g. the technologist tabs into a findings field) as long as required
  // fields are already valid — saves the extra manual "Collapse" click for
  // the common case of filling patient info top-to-bottom then moving on.
  // The button stays for anyone who wants to collapse without navigating
  // away, or re-collapse after using "Edit".
  const handleBlur = (e) => {
    if (!hasAllRequired || hasRequiredFieldError) return;
    const next = e.relatedTarget;
    if (!next || (containerRef.current && !containerRef.current.contains(next))) {
      setCollapsed(true);
    }
  };

  if (isCollapsed) {
    return (
      <div className="form-section patient-info patient-info-collapsed">
        <div className="patient-info-summary">
          <span className="patient-info-summary-text">
            <strong>{formData.lastName}, {formData.firstName}</strong>
            {' — MRN '}{formData.mrn}
            {' — '}{formatExamDate(formData.examDate)}
            {formData.technologist ? ` — ${technologistLabel}: ${formData.technologist}` : ''}
            {needsSupervisor && supervisor ? ` — Supervisor: ${supervisor}` : ''}
          </span>
          <button
            type="button"
            className="patient-info-edit-btn"
            onClick={() => setCollapsed(false)}
          >
            Edit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-section patient-info" ref={containerRef} onBlur={handleBlur}>
      <div className="patient-info-header-row">
        <h3>Patient Information</h3>
        <button
          type="button"
          className={`patient-info-collapse-btn${flashCollapseBtn ? ' patient-info-collapse-btn-flash' : ''}`}
          onClick={() => setCollapsed(true)}
          disabled={!hasAllRequired}
          title={hasAllRequired ? 'Collapse patient information' : 'Fill in required fields to enable collapsing'}
        >
          Collapse
        </button>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Exam Date: *</label>
          <input
            type="date"
            value={formData.examDate}
            onChange={(e) => updateField('examDate', e.target.value)}
            className={fieldErrors.examDate ? 'input-error' : ''}
            required
          />
          {fieldErrors.examDate && (
            <span className="field-error">{fieldErrors.examDate}</span>
          )}
        </div>

        <div className="form-group">
          <label>Exam Type:</label>
          <input type="text" value={examLabel || formData.examType || ''} disabled />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>First Name: *</label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => updateField('firstName', e.target.value)}
            className={fieldErrors.firstName ? 'input-error' : ''}
            required
          />
          {fieldErrors.firstName && (
            <span className="field-error">{fieldErrors.firstName}</span>
          )}
        </div>

        <div className="form-group">
          <label>Last Name: *</label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => updateField('lastName', e.target.value)}
            className={fieldErrors.lastName ? 'input-error' : ''}
            required
          />
          {fieldErrors.lastName && (
            <span className="field-error">{fieldErrors.lastName}</span>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>MRN: *</label>
          <input
            type="text"
            value={formData.mrn}
            onChange={(e) => updateField('mrn', e.target.value)}
            className={fieldErrors.mrn ? 'input-error' : ''}
            required
          />
          {fieldErrors.mrn && (
            <span className="field-error">{fieldErrors.mrn}</span>
          )}
        </div>

        <div className="form-group">
          <label>{technologistLabel}: *</label>
          <select
            value={formData.technologist}
            onChange={(e) => updateField('technologist', e.target.value)}
            required
          >
            <option value="">Select {technologistLabel}</option>
            {technologists && technologists.map(tech => (
              <option key={tech} value={tech}>{tech}</option>
            ))}
          </select>
        </div>
      </div>

      {needsSupervisor && (
        <div className="form-row">
          <div className="form-group">
            <label>Supervising Sonographer: *</label>
            <select
              value={showSupervisorOther ? SUPERVISOR_OTHER : supervisor}
              onChange={(e) => {
                if (e.target.value === SUPERVISOR_OTHER) {
                  setSupervisorOther(true);
                  updateField('supervisingSonographer', '');
                } else {
                  setSupervisorOther(false);
                  updateField('supervisingSonographer', e.target.value);
                }
              }}
              className={fieldErrors.supervisingSonographer && !showSupervisorOther ? 'input-error' : ''}
              required
            >
              <option value="">Select Supervising Sonographer</option>
              {supervisors.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
              <option value={SUPERVISOR_OTHER}>Other (type name)</option>
            </select>
            {fieldErrors.supervisingSonographer && !showSupervisorOther && (
              <span className="field-error">{fieldErrors.supervisingSonographer}</span>
            )}
          </div>

          {showSupervisorOther && (
            <div className="form-group">
              <label>Supervisor Name: *</label>
              <input
                type="text"
                value={supervisor}
                onChange={(e) => updateField('supervisingSonographer', e.target.value)}
                placeholder="First and last name"
                className={fieldErrors.supervisingSonographer ? 'input-error' : ''}
                required
              />
              {fieldErrors.supervisingSonographer && (
                <span className="field-error">{fieldErrors.supervisingSonographer}</span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="form-group">
        <label>Reason for Exam:</label>
        <input
          type="text"
          value={formData.reason}
          onChange={(e) => updateField('reason', e.target.value)}
          placeholder="e.g., Hematuria, follow-up"
        />
      </div>

      {showCallReport && (
        <div className="call-report-section">
          <label>
            <input
              type="checkbox"
              checked={formData.callReport}
              onChange={(e) => updateField('callReport', e.target.checked)}
            />
            Call Report
          </label>

          {formData.callReport && (
            <div className="form-group">
              <label>Phone Number:</label>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => updateField('phoneNumber', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PatientInfo;
